import { useEffect } from 'react'

/**
 * Keeps the screen from sleeping while `isActive`. A phone that locks suspends
 * the page, and with it the microphone and the socket, so a live call holds the
 * screen on instead. The browser drops the lock whenever the page is hidden, so
 * it is asked for again each time the page comes back. Unsupported or refused:
 * nothing happens, and the screen sleeps as it normally would.
 */
const useScreenWakeLock = (isActive: boolean): void => {
  useEffect(() => {
    if (!isActive || !('wakeLock' in navigator)) return undefined
    let sentinel: WakeLockSentinel | null = null
    let isCurrent = true

    const acquire = async (): Promise<void> => {
      if (document.visibilityState !== 'visible' || (sentinel && !sentinel.released)) return
      try {
        const next = await navigator.wakeLock.request('screen')
        if (isCurrent) sentinel = next
        else void next.release()
      } catch {
        // Refused (low battery, a policy): the call goes on without it.
      }
    }
    const handleVisibility = (): void => {
      void acquire()
    }

    void acquire()
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      isCurrent = false
      document.removeEventListener('visibilitychange', handleVisibility)
      void sentinel?.release()
    }
  }, [isActive])
}

export default useScreenWakeLock
